import React, { createContext, useContext, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEY } from "@/constants/query-key";
import { getMeRequest } from "@/api/auth";
import type { Me } from "@/types/auth";

type AuthType = {
  isAuthenticated: boolean;
  isLoading: boolean;
  address: string | null;
  user: Me | null;
  resetAuth: () => void;
};

const AuthContext = createContext<AuthType | null>(null);

export const useAuth = (): AuthType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthContext");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, authenticated, ready } = usePrivy();
  const [address, setAddress] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  const {
    data: userData,
    isLoading,
  } = useQuery({
    queryKey: [QUERY_KEY.ME],
    queryFn: async () => {
      const me = await getMeRequest();
      setAddress(user?.wallet?.address ?? me.address);
      setIsAuthenticated(true);
      return me;
    },
    enabled: authenticated && ready && !!user,
    retry: false,
  });

  const resetAuth = () => {
    setAddress(null);
    setIsAuthenticated(false);
  };

  const auth = {
    isAuthenticated,
    isLoading: isLoading || !ready,
    address,
    user: userData ?? null,
    resetAuth,
  };

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
